import i18n from 'i18next';
import { create } from "zustand";
import { formService } from "@/services";
import { isPreviewMode } from "@/lib/previewMode";
import { useGuestRegistrationStore } from "./guestRegistrationStore";
import {
    FormData,
    FormMetadata,
    FormState,
    FileUploadTracker,
    BookingDetails,
    ContractState,
    ContractSigningState,
    ContractSignature,
    GuestData,
} from "@/types/form";
import {
    PublicFormDataApiDto,
    PublicFormDataAndVersionApiDto,
    VersionApiDto,
    FormStatusApiEnum,
    ContractApiDto,
    ContractSignatureApiDto,
    MimeApiEnum,
} from "@/types/api";
import { UIStore } from "@/types/store";
import type { TFunction } from "i18next";
import {
    getViolations,
    errorLogDetail,
    resolveErrorMessage,
} from "@/services/api/validationErrors";
import { dlog, summarizeMatch } from "@/utils/debugProfileMatch";
import {
    convertApiFormDataToLocal,
    convertLocalFormDataToApi,
} from "@/utils/transformApiData";
import { UploadedFileInfo } from "@/types/form";
import type { FormGuestEntry, ProfileMatch } from "@/types/guest";

interface GeneralFormStoreState {
    formData: FormData | null;
    formMetadata: FormMetadata | null;
    bookingDetails: BookingDetails | null;
    formState: FormState;
    fileUploadTracker: FileUploadTracker;
    formId: string;
    contractState: ContractState;
    contractSigningState: ContractSigningState;
    uiStore: UIStore | null;

    // Owner's branded subdomain hint emitted by _getFormData when the form's listing's
    // Owner has an active Business-tier brand. FormDataBrandRedirect subscribes to
    // this slice to issue the canonical→branded redirect, gated additionally by
    // brandAutoRedirect; null when the form's Owner has no brand or is below Business tier.
    brandSlug: string | null;

    // Owner's opt-out of the canonical→branded force-redirect. Gates the FORWARD redirect
    // only (reverse + slug-history redirects are never gated). Defaults true (column
    // DEFAULT TRUE); harmless before the fetch settles because the forward branch also
    // requires brandSlug.
    brandAutoRedirect: boolean;

    // WHY: FormDataBrandRedirect uses this as the load sentinel. brandSlug alone is
    // ambiguous — it's null both before the fetch completes AND after a successful
    // fetch on a Basic-tier listing — so the redirect cannot interpret null as
    // "no brand" without first knowing the fetch settled. formId is set BEFORE the
    // fetch starts (line ~124) so it can't be the sentinel; formMetadata flips on
    // success but stays null on the error path, trapping the reverse redirect on
    // failed fetches. formLoaded flips to true on BOTH the success and error
    // terminals so the redirect is unblocked either way.
    formLoaded: boolean;

    // Story 20-2 Phase 1: per-entry returning-guest profile match (in-memory only,
    // no localStorage / IndexedDB / persist middleware — wiped on browser refresh).
    profileMatchByEntryId: Record<string, ProfileMatch>;
    // Story 20-2 Phase 1: per-entry edited-field set the user typed away from the
    // prefilled value. Set<string> is not JSON-serializable so this slice MUST stay
    // unpersisted.
    editedFieldsByEntryId: Record<string, Set<string>>;
    /**
     * Entries whose profile photos have already been OFFERED to the server: a PUT carried
     * `profileApplied: true` and the follow-up refresh came back successfully.
     *
     * Memory only, and deliberately so. It is the record of one honest attempt per page session:
     * after it, a required slot with no booking row is MISSING and the guest is asked to upload,
     * rather than being shown a tick for a photo the server declined to attach and then refused at
     * the signature. A reload clears it and the next Next retries — the server-side attach is
     * idempotent.
     */
    profilePhotosOfferedByEntryId: Record<string, true>;

    // Actions
    initializeForm: (publicId: string, guestEntries?: FormGuestEntry[]) => Promise<void>;
    saveForm: () => Promise<void>;
    submitForm: () => Promise<void>;
    checkVersion: (signal?: AbortSignal) => Promise<boolean>;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    reset: () => void;
    updateFromServer: (serverData: PublicFormDataAndVersionApiDto, guestEntries?: FormGuestEntry[]) => void;
    updateGuestLocal: (entryId: string, data: Partial<GuestData>) => void;
    updateGuestFiles: (entryId: string, files: UploadedFileInfo[]) => void;
    updateGuestFilesFromServer: (entryId: string, serverData: GuestFilesRefreshPayload) => void;
    updateFormStatus: (status: FormStatusApiEnum) => void;
    loadContract: (signal?: AbortSignal) => Promise<void>;
    signContract: (signature: ContractSignature) => Promise<void>;
    setContractError: (error: string | null) => void;
    /**
     * Returns TRUE only when the refresh actually wrote new server data into the store.
     *
     * It used to return void and swallow every non-404 failure, invalid response and abort, so a
     * caller could not tell a landed refresh from a silent no-op. FC-2 depends on the difference:
     * marking a guest's profile photos as "offered" after a refresh that never happened would
     * flip their slots to MISSING on stale data and ask them to re-upload photos the server may
     * well have attached.
     */
    refreshFormData: (publicId: string, signal?: AbortSignal, guestEntries?: FormGuestEntry[]) => Promise<boolean>;
    resetContractState: () => void;
    updateGuestDesignation: (entryId: string) => void;
    // Story 20-2 Phase 1.
    setProfileMatch: (entryId: string, match: ProfileMatch | null) => void;
    addEditedField: (entryId: string, fieldName: string) => void;
    // Story 20-5 Phase 3 — Cancel-without-typing data-wipe-trap fix (AC #6).
    removeEditedField: (entryId: string, fieldName: string) => void;
    // Claim 1 follow-up — drop the entire edited-fields slot for an entry. Used by
    // useResetProfileMatchOnIdentityEdit when the lookup-key triple changes after POST.
    clearEditedFields: (entryId: string) => void;
    /** FC-2: record that this entry's profile photos have had their one honest attempt. */
    markProfilePhotosOffered: (entryId: string) => void;
}

/**
 * What {@link GeneralFormStoreState.updateGuestFilesFromServer} needs from a post-mutation refetch.
 *
 * Widened past `PublicFormDataAndVersionApiDto` because that DTO carries no `overallProgress`: the
 * upload/remove refetch used to copy the new `files[]` and leave both the guest's `progress` and
 * the form's `overallProgress` at their pre-upload values, so the gate that reads them stayed shut
 * until the next poll.
 */
export interface GuestFilesRefreshPayload extends PublicFormDataAndVersionApiDto {
    overallProgress?: number;
}

export const useGeneralFormStore = create<GeneralFormStoreState>((set, get) => ({
    formData: null,
    formMetadata: null,
    bookingDetails: null,
    formState: {
        isLoading: false,
        isSaving: false,
        isSubmitting: false,
        hasUnsavedChanges: false,
        lastSavedVersion: "",
        currentVersion: "",
        error: undefined,
        currentSection: "",
        sections: {},
        versionMismatch: false,
    },
    fileUploadTracker: {},
    formId: "",
    uiStore: null,
    profileMatchByEntryId: {},
    editedFieldsByEntryId: {},
    profilePhotosOfferedByEntryId: {},
    brandSlug: null,
    brandAutoRedirect: true,
    formLoaded: false,
    contractState: {
        isLoading: false,
        data: null,
        error: null,
        violations: [],
    },
    contractSigningState: {
        isLoading: false,
        isSigning: false,
        error: null,
        violations: [],
    },

    initializeForm: async (publicId: string, guestEntries?: FormGuestEntry[]) => {
        // # WHY: PreviewDataProvider hydrates this store synchronously before the
        // children mount; bailing here keeps the FormPage useEffect path identical
        // in preview mode without touching real services or flipping isLoading.
        if (isPreviewMode()) return;

        set((state) => ({
            formState: { ...state.formState, isLoading: true },
        }));

        try {
            set({ formId: publicId });

            const serverResponse: PublicFormDataApiDto = await formService.getForm(publicId);

            if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                console.error('[initializeForm] Invalid server response structure', { serverResponse });
                throw new Error('Invalid server response: Missing required fields');
            }

            set({ brandSlug: serverResponse.brandSlug ?? null });
            set({ brandAutoRedirect: serverResponse.brandAutoRedirect ?? true });

            get().updateFromServer({
                data: serverResponse.data,
                version: serverResponse.version,
            }, guestEntries);

            const formMetadata: FormMetadata = {
                formId: publicId,
                publicId: publicId,
                templateId: serverResponse.templateId,
                listingTitle: serverResponse.listingTitle,
                checkInDate: serverResponse.checkInDate,
                checkOutDate: serverResponse.checkOutDate,
                numberOfGuests: serverResponse.numberOfGuests,
                maxVehicles: serverResponse.maxVehicles,
                allowPets: serverResponse.allowPets,
                listingAddress: serverResponse.listingAddress,
                listingCity: serverResponse.listingCity,
                listingCountry: serverResponse.listingCountry,
                latitude: serverResponse.latitude,
                longitude: serverResponse.longitude,
                status: serverResponse.status,
                version: serverResponse.version,
                requiredGuestImages: serverResponse.requiredGuestImages,
                requiredBookingContract: serverResponse.requiredBookingContract,
                signedContract: serverResponse.signedContract,
                minAgeToRequireImages: serverResponse.minAgeToRequireImages,
                requireContactDataFromAllGuests: serverResponse.requireContactDataFromAllGuests,
                // Migration 0160: normalize the wire's optional booleans to concrete ones
                // exactly once, here. A backend that predates 0160 omits them entirely, and
                // absent must mean "unrestricted" — the pre-0160 behaviour — not "unknown".
                requirePassportForForeignGuests: serverResponse.requirePassportForForeignGuests === true,
                requireNationalIdForDomesticGuests: serverResponse.requireNationalIdForDomesticGuests === true,
                overallProgress: serverResponse.overallProgress,
                contractStatus: serverResponse.contractStatus,
                rejectionReason: serverResponse.rejectionReason,
                // Story 28-4: full bilingual + multi-status messages map.
                // Always present on the wire (possibly empty). FormPage
                // resolves (status, locale) at render time.
                formMessages: serverResponse.formMessages ?? {},
                // Story 42-15: the ID photo check. `=== true`, so a backend that predates the
                // field (or sends anything but true) means OFF: no id-scans call at all.
                idScanEnabled: serverResponse.idScanEnabled === true,
            };
            const bookingDetails: BookingDetails = {
                listingTitle: serverResponse.listingTitle,
                checkInDate: serverResponse.checkInDate,
                checkOutDate: serverResponse.checkOutDate,
                numberOfGuests: serverResponse.numberOfGuests,
                maxVehicles: serverResponse.maxVehicles,
                allowPets: serverResponse.allowPets,
                listingAddress: serverResponse.listingAddress,
                listingCity: serverResponse.listingCity,
                listingCountry: serverResponse.listingCountry,
                latitude: serverResponse.latitude,
                longitude: serverResponse.longitude,
            };

            set((state) => ({
                formMetadata,
                bookingDetails,
                formState: {
                    ...state.formState,
                    currentVersion: serverResponse.version,
                    lastSavedVersion: serverResponse.version,
                },
            }));
        } catch (error) {
            console.error("Error initializing form", error);
            get().setError("Failed to initialize form.");
            throw error;
        } finally {
            set((state) => ({
                formState: { ...state.formState, isLoading: false },
                formLoaded: true,
            }));
        }
    },

    saveForm: async () => {
        if (isPreviewMode()) return;
        set((state) => ({
            formState: { ...state.formState, isSaving: true },
        }));

        try {
            const { formData, formId, formState, uiStore } = get();
            if (!formData) {
                console.error("No form data to save");
                return;
            }

            const apiData = convertLocalFormDataToApi(formData);
            const response: VersionApiDto = await formService.updateFormData(formId, {
                data: apiData,
                version: formState.currentVersion,
            });

            set((state) => ({
                formState: {
                    ...state.formState,
                    currentVersion: response.version,
                    lastSavedVersion: response.version,
                },
            }));

            // Save succeeded. Refresh form data to sync contract validity (hash may have
            // changed). NOTE: refreshFormData only re-throws NOT_FOUND errors (other errors
            // are swallowed internally), so the catch here only fires when the form was
            // deleted server-side between save and refresh. In that case the save worked
            // but the form is gone — let the FormPage polling/visibility handler navigate
            // the user to the not-found state by re-throwing. The save success toast is
            // skipped because the outer catch will fire instead.
            // # WHY guestEntries: refreshFormData feeds convertApiFormDataToLocal, which
            // resolves real UUIDs for formData.guests[i].entryId only when entries are
            // provided — otherwise it falls back to legacy_<num> placeholders that don't
            // match useGuestRegistrationStore.guests[i].id, breaking the GuestSection
            // per-card progress lookup until the next poll/visibility refresh.
            try {
                const guestEntries = useGuestRegistrationStore.getState().guests;
                // Fresh signal: `apiClient.get` merges identical in-flight GETs only when none is
                // given, so a refresh racing the page's 30s poll would be served that poll's
                // PRE-SAVE response and quietly overwrite the write we just made.
                await get().refreshFormData(formId, new AbortController().signal, guestEntries);
            } catch (refreshError) {
                console.warn(
                    "[saveForm] Save succeeded but refresh reported NOT_FOUND — form was deleted server-side",
                    refreshError,
                );
                throw refreshError;
            }

            uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.saved.title"),
                message: i18n.t("toasts.form.saved.message"),
                duration: 3000,
            });
        } catch (error) {
            console.error("Error saving form", error);
            get().setError("Failed to save form.");
            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.saveError.title"),
                message: i18n.t("toasts.form.saveError.message"),
                duration: 3000,
            });
        } finally {
            set((state) => ({
                formState: { ...state.formState, isSaving: false },
            }));
        }
    },

    submitForm: async () => {
        if (isPreviewMode()) return;
        set((state) => ({
            formState: { ...state.formState, isSubmitting: true },
        }));

        try {
            const { formId, formState } = get();

            await formService.completeForm(formId, {
                version: formState.currentVersion,
            });

            get().updateFormStatus(FormStatusApiEnum.COMPLETED);

            set((state) => ({
                formState: {
                    ...state.formState,
                    hasUnsavedChanges: false,
                },
            }));

            get().uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.submitted.title"),
                message: i18n.t("toasts.form.submitted.message"),
                duration: 3000,
            });

        } catch (error: unknown) {
            // Codes only — the body carries `args.guestId`, which is a document number.
            console.error("Error submitting form", errorLogDetail(error));
            // Deliberately NOT get().setError(): `formState.error` is global and only ever cleared
            // by reset(), so a failed submit left a sticky error behind that greeted the guest
            // with a bogus "Synchronization Issue" toast on the next guest page they opened. The
            // submit surface renders the translated violations itself (FC-6).
            throw error;
        } finally {
            set((state) => ({
                formState: { ...state.formState, isSubmitting: false },
            }));
        }
    },

    checkVersion: async (signal?: AbortSignal) => {
        if (isPreviewMode()) return true;
        try {
            const { formId, formState, uiStore } = get();

            if (!formId) return true;
            if (!formState.currentVersion || formState.currentVersion === "") return true;

            const response = await formService.isLatestFormDataVersion(formId, {
                version: formState.currentVersion,
            }, signal);

            if (signal?.aborted) return true;

            if (!response.result) {
                console.log('[checkVersion] Version mismatch detected, fetching latest data');

                try {
                    const serverResponse = await formService.getForm(formId, signal);
                    if (signal?.aborted) return true;

                    if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                        throw new Error('Invalid server response: Missing required fields');
                    }

                    // convertApiFormDataToLocal now resolves entryId from each guest's own
                    // guestEntryId, so the entryId is already authoritative. Only fall back to
                    // correlating against existing local guests (by guestNumId) when the server
                    // response lacked guestEntryId and left a legacy_* placeholder — never clobber
                    // a real entryId (a guestNumId match can be wrong after a renumber).
                    const existingGuests = get().formData?.guests;
                    const convertedData = convertApiFormDataToLocal(serverResponse.data);
                    if (existingGuests) {
                        convertedData.guests = convertedData.guests.map(g => {
                            if (!g.entryId.startsWith('legacy_')) return g;
                            const existing = existingGuests.find(e => e.guestNumId === g.guestNumId);
                            return existing ? { ...g, entryId: existing.entryId } : g;
                        });
                    }
                    set({ formData: convertedData });

                    set((state) => ({
                        formState: {
                            ...state.formState,
                            currentVersion: serverResponse.version,
                            lastSavedVersion: serverResponse.version,
                        },
                    }));

                    return false;
                } catch (conversionError: unknown) {
                    console.error('[checkVersion] Failed to process server data:', conversionError);
                    uiStore?.addToast({
                        type: "warning",
                        title: i18n.t("toasts.form.syncIssue.title"),
                        message: i18n.t("toasts.form.syncIssue.message"),
                        duration: 3000,
                    });
                    return true;
                }
            }

            return true;
        } catch (error: unknown) {
            const errObj = error as { name?: string; response?: unknown; code?: string; message?: string };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                return true;
            }

            console.error("[checkVersion] Error checking version", error);

            const isNetworkError =
                !errObj.response ||
                errObj.code === 'ECONNABORTED' ||
                errObj.message?.toLowerCase().includes('network');

            if (!isNetworkError) {
                get().uiStore?.addToast({
                    type: "warning",
                    title: i18n.t("toasts.form.versionCheckIssue.title"),
                    message: i18n.t("toasts.form.versionCheckIssue.message"),
                    duration: 3000,
                });
            }

            return true;
        }
    },

    setLoading: (loading: boolean) => {
        set((state) => ({
            formState: { ...state.formState, isLoading: loading },
        }));
    },

    setError: (error: string | null) => {
        set((state) => {
            const newFormState = { ...state.formState };
            if (error) {
                newFormState.error = { field: "", message: error, type: "invalid" };
            } else {
                delete newFormState.error;
            }
            return { formState: newFormState };
        });
    },

    reset: () => {
        const { uiStore } = get();

        set({
            formId: "",
            formData: null,
            formMetadata: null,
            bookingDetails: null,
            formState: {
                isLoading: false,
                isSaving: false,
                isSubmitting: false,
                hasUnsavedChanges: false,
                lastSavedVersion: "",
                currentVersion: "",
                error: undefined,
                currentSection: "",
                sections: {},
                versionMismatch: false,
            },
            fileUploadTracker: {},
            // Story 20-2 Phase 1: clear in-memory Guest Memory state on form reset.
            profileMatchByEntryId: {},
            editedFieldsByEntryId: {},
            // FC-2: a fresh form gets a fresh honest attempt per entry.
            profilePhotosOfferedByEntryId: {},
            brandSlug: null,
            brandAutoRedirect: true,
            formLoaded: false,
            contractState: {
                isLoading: false,
                data: null,
                error: null,
                violations: [],
            },
            contractSigningState: {
                isLoading: false,
                isSigning: false,
                error: null,
                violations: [],
            },
        });

        uiStore?.addToast({
            type: "info",
            title: i18n.t("toasts.form.reset.title"),
            message: i18n.t("toasts.form.reset.message"),
            duration: 3000,
        });
    },

    updateFromServer: (serverData: PublicFormDataAndVersionApiDto, guestEntries?: FormGuestEntry[]) => {
        const { uiStore } = get();

        try {
            if (!serverData) throw new Error('Invalid server response: No data received');
            if (!serverData.data) throw new Error('Invalid server response: Missing data field');
            if (!serverData.version) throw new Error('Invalid server response: Missing version field');

            const newLocalData = convertApiFormDataToLocal(serverData.data, guestEntries);

            // Story 20-1 Phase 1 (refresh-resilient prefill): hydrate profileMatchByEntryId
            // from the public form GET response. Each guest in apiData.{mainGuest,otherGuests}
            // now carries an optional profileMatch (attached by PublicApiImpl._getFormData
            // per guest via lookupProfileMatchByKey). We pair API guests with FE entry IDs
            // using the same logic as convertApiFormDataToLocal so the keying matches.
            const hydratedProfileMatches: Record<string, ProfileMatch> = {};
            if (guestEntries && guestEntries.length > 0) {
                const mainEntry = guestEntries.find(e => e.isMainGuest);
                const nonMainEntries = guestEntries.filter(e => !e.isMainGuest);
                // Pair by the stable entry UUID (guestEntryId); fall back to the guestNumId
                // join only for responses predating guestEntryId. Same identity-based fix as
                // convertApiFormDataToLocal — positional/guestNumId pairing landed a matched
                // guest's photo flags on the wrong entryId (guest image-reuse bug).
                const mainEntryId = serverData.data.mainGuest?.guestEntryId || mainEntry?.id;
                if (serverData.data.mainGuest && mainEntryId && serverData.data.mainGuest.profileMatch) {
                    hydratedProfileMatches[mainEntryId] = serverData.data.mainGuest.profileMatch;
                }
                (serverData.data.otherGuests || []).forEach((g) => {
                    const entryId = g.guestEntryId || nonMainEntries.find(e => e.guestNumId === g.guestNumId)?.id;
                    if (entryId && g.profileMatch) {
                        hydratedProfileMatches[entryId] = g.profileMatch;
                    }
                });
            }

            dlog("store.updateFromServer", "hydrated from form GET", {
                gotGuestEntries: guestEntries ? guestEntries.length : "NONE (no hydration possible)",
                apiGuestEntryIds: [
                    serverData?.data?.mainGuest?.guestEntryId,
                    ...(serverData?.data?.otherGuests || []).map((g) => g.guestEntryId),
                ].filter(Boolean),
                // Defensive on purpose: a debug line must never be the thing that throws. Callers
                // and tests can hand `updateFromServer` a partial shape, and instrumentation that
                // assumes a full one turns a diagnostic into an outage.
                localGuestEntryIds: (newLocalData?.guests ?? []).map((g) => g.entryId),
                matchesHydratedFor: Object.keys(hydratedProfileMatches),
                perGuestPhotos: Object.fromEntries(
                    Object.entries(hydratedProfileMatches).map(([id, m]) => [id, summarizeMatch(m).photos]),
                ),
                filesPerGuest: Object.fromEntries(
                    (newLocalData?.guests ?? []).map((g) => [g.entryId, (g.files || []).map((f) => f.type)]),
                ),
            });

            set((state) => ({
                formData: newLocalData,
                formState: {
                    ...state.formState,
                    currentVersion: serverData.version,
                    lastSavedVersion: serverData.version,
                },
                // Merge hydrated matches over any in-memory ones (post-POST setProfileMatch
                // already populated entries, but a refresh comes through this path too).
                profileMatchByEntryId: {
                    ...state.profileMatchByEntryId,
                    ...hydratedProfileMatches,
                },
            }));
        } catch (error: unknown) {
            console.error('[updateFromServer] Failed to update from server:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to update form data from server';
            get().setError(errorMessage);

            uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.updateFailed.title"),
                message: i18n.t("toasts.form.updateFailed.message"),
                duration: 5000,
            });

            throw error;
        }
    },

    /**
     * Update a single guest's local data by entryId.
     * Replaces updateMainGuestLocal + updateOtherGuestLocal.
     */
    updateGuestLocal: (entryId: string, data: Partial<GuestData>) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g =>
                        g.entryId === entryId ? { ...g, ...data } : g
                    ),
                },
                formState: {
                    ...state.formState,
                    hasUnsavedChanges: true,
                },
            };
        });
    },

    /**
     * Flip the isPrimaryContact flag — sets one guest as primary,
     * clears the flag on all others. No data reshuffling.
     */
    updateGuestDesignation: (entryId: string) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g => ({
                        ...g,
                        isPrimaryContact: g.entryId === entryId,
                    })),
                },
            };
        });
    },

    updateFormStatus: (status: FormStatusApiEnum) => {
        set((state) => ({
            formMetadata: state.formMetadata ? {
                ...state.formMetadata,
                status
            } : null
        }));
    },

    updateGuestFiles: (entryId: string, files: UploadedFileInfo[]) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g =>
                        g.entryId === entryId ? { ...g, files } : g
                    ),
                },
            };
        });
    },

    updateGuestFilesFromServer: (entryId: string, serverData: GuestFilesRefreshPayload) => {
        const { formData: localData } = get();
        if (!localData) return;

        const targetGuest = localData.guests.find(g => g.entryId === entryId);
        if (!targetGuest) return;

        // convertApiFormDataToLocal resolves entryId from guestEntryId, so match the target
        // guest by its stable entryId; fall back to guestNumId only for legacy responses.
        const newLocalData = convertApiFormDataToLocal(serverData.data);
        const serverGuest = newLocalData.guests.find(g => g.entryId === entryId)
            ?? newLocalData.guests.find(g => g.guestNumId === targetGuest.guestNumId);

        // The guest's PROFILE match, refreshed alongside the files.
        //
        // The backend now advances a guest's profile photo to whatever they just uploaded, so the
        // `profileMatch` on this response can be newer than the one in the store — which was
        // captured at POST and, before this, only ever re-read by `updateFromServer` (initial load
        // and the 30s poll). An upload went through THIS reducer instead, so the banner and the
        // ID-verification slots kept describing the profile as it was when the guest arrived:
        // a photo they had just added still read as "not on file" until a full page reload.
        //
        // Only when the response actually carries one. A response without a match means "no
        // profile" or "not looked up", neither of which is a reason to drop a match we hold.
        const apiGuests = [
            serverData.data.mainGuest,
            ...(serverData.data.otherGuests || []),
        ].filter(Boolean) as Array<{ guestEntryId?: string; guestNumId?: number; profileMatch?: ProfileMatch }>;
        const apiGuest = apiGuests.find(g => g.guestEntryId === entryId)
            ?? apiGuests.find(g => g.guestNumId === targetGuest.guestNumId);
        const freshMatch = apiGuest?.profileMatch;

        dlog("store.updateGuestFilesFromServer", "post-upload/remove refresh", {
            entryId,
            serverFiles: (serverGuest?.files || []).map((f) => f.type),
            progress: serverGuest?.progress,
            overallProgress: serverData.overallProgress,
            matchOnResponse: freshMatch ? summarizeMatch(freshMatch).photos : "none (keeping stored match)",
        });

        // Copy `progress` alongside the files, and the form's `overallProgress` alongside that.
        // Replacing `files` only left every gate that reads progress — the section tick, the
        // submit button, the signing gate — looking at the value from BEFORE the upload, until
        // the next 30s poll happened to correct it.
        set((state) => ({
            formData: {
                ...state.formData!,
                guests: state.formData!.guests.map(g =>
                    g.entryId === entryId && serverGuest
                        ? {
                            ...g,
                            files: serverGuest.files,
                            ...(serverGuest.progress !== undefined
                                ? { progress: serverGuest.progress }
                                : {}),
                        }
                        : g
                ),
            },
            formMetadata: state.formMetadata && serverData.overallProgress !== undefined
                ? { ...state.formMetadata, overallProgress: serverData.overallProgress }
                : state.formMetadata,
            formState: {
                ...state.formState,
                currentVersion: serverData.version,
                lastSavedVersion: serverData.version,
            },
            profileMatchByEntryId: freshMatch
                ? { ...state.profileMatchByEntryId, [entryId]: freshMatch }
                : state.profileMatchByEntryId,
        }));
    },

    loadContract: async (signal?: AbortSignal) => {
        if (isPreviewMode()) return;
        const { formId } = get();
        set((state) => ({
            contractState: { ...state.contractState, isLoading: true, error: null }
        }));

        try {
            const contractData: ContractApiDto = await formService.getContract(formId, signal);
            if (signal?.aborted) return;

            set(() => ({
                contractState: {
                    isLoading: false,
                    data: contractData,
                    error: null,
                    violations: [],
                }
            }));
        } catch (error: unknown) {
            const errObj = error as { name?: string; response?: { data?: { message?: string } }; message?: string };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                set((state) => ({
                    contractState: { ...state.contractState, isLoading: false }
                }));
                return;
            }

            console.error("Error loading contract", errorLogDetail(error));
            // Translate the server's CODES. `message` is English-only developer prose, so showing
            // it to a guest is worse than showing translated generic copy (docs/i18n-conventions).
            const t = i18n.t.bind(i18n) as unknown as TFunction;
            // Keep the CODES as data, exactly as `signContract` and `submitForm` do. FC-6 names
            // contract load as one of the four surfaces that must group a refusal per guest and
            // link to them; storing only the flat sentence left this one showing a single line
            // that names nobody when several guests are each missing a document.
            const violations = getViolations(error);
            const errorMessage = resolveErrorMessage(error, t);
            set(() => ({
                contractState: {
                    isLoading: false,
                    data: null,
                    error: errorMessage,
                    violations,
                }
            }));

            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.contractLoadError.title"),
                message: errorMessage,
                duration: 3000,
            });
        }
    },

    signContract: async (signature: ContractSignature) => {
        if (isPreviewMode()) return;
        const { formId } = get();
        set((state) => ({
            contractSigningState: {
                ...state.contractSigningState,
                isSigning: true,
                error: null,
                violations: [],
            }
        }));

        try {
            const contentType = signature.signatureFile.type as MimeApiEnum;

            const { url, key } = await formService.getContractSignPresignedUrl(formId, {
                contentType: contentType
            });

            const uploadResponse = await fetch(url, {
                method: 'PUT',
                body: signature.signatureFile,
                headers: {
                    'Content-Type': signature.signatureFile.type,
                },
            });

            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('[signContract] S3 upload failed:', errorText);
                throw new Error(`S3 upload failed with status: ${uploadResponse.status}`);
            }

            const signatureData: ContractSignatureApiDto = {
                formVersion: signature.formVersion,
                s3Key: key,
                contentType: contentType,
                ipAddress: signature.ipAddress,
                deviceInfo: signature.deviceInfo,
            };

            await formService.signContract(formId, signatureData);

            // Signing succeeded. Refresh from server to pick up actual contract validity
            // (the hash-based check runs server-side). Wrap in its own try/catch so a
            // transient refresh failure does NOT surface as a signing failure. NOTE:
            // refreshFormData only re-throws NOT_FOUND errors (other errors are swallowed
            // internally), so the catch here only fires when the form was deleted server-side
            // between sign and refresh. In that case the optimistic "signed=true" would be
            // misleading — the form is gone — so we re-throw to let the FormPage polling /
            // visibility-change handler navigate the user to the not-found state.
            // # WHY guestEntries: without entries, convertApiFormDataToLocal falls back to
            // legacy_<num> entryId placeholders that don't match useGuestRegistrationStore
            // UUIDs — every guest card flashes "Sección pendiente" until the next poll.
            try {
                const guestEntries = useGuestRegistrationStore.getState().guests;
                // Fresh signal, same reason as every other post-write refetch: a merged response
                // that left the server before the signature would report the contract unsigned.
                await get().refreshFormData(formId, new AbortController().signal, guestEntries);
            } catch (refreshError) {
                console.warn(
                    "[signContract] Sign succeeded but refresh reported NOT_FOUND — form was deleted server-side",
                    refreshError,
                );
                // Do NOT optimistically set signedContract: true — the form no longer exists.
                // Re-throw so the outer catch surfaces the not-found state to the user.
                throw refreshError;
            }

            set(() => ({
                contractSigningState: {
                    isLoading: false,
                    isSigning: false,
                    error: null,
                    violations: [],
                }
            }));

            get().uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.contractSigned.title"),
                message: i18n.t("toasts.form.contractSigned.message"),
                duration: 3000,
            });
        } catch (error: unknown) {
            // Codes and status only. `guest.file.missing` carries the guest's DOCUMENT NUMBER in
            // `args.guestId`, and this line used to print the whole response body.
            console.error("Error signing contract", errorLogDetail(error));
            const t = i18n.t.bind(i18n) as unknown as TFunction;
            // Keep the CODES as data so the modal can group them by guest and link to the guest
            // who still owes a document; the flat sentence is the fallback for the toast.
            const violations = getViolations(error);
            const errorMessage = resolveErrorMessage(error, t);
            set(() => ({
                contractSigningState: {
                    isLoading: false,
                    isSigning: false,
                    error: errorMessage,
                    violations,
                }
            }));

            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.contractSigningError.title"),
                message: errorMessage,
                duration: 3000,
            });
            throw error;
        }
    },

    setContractError: (error: string | null) => {
        set((state) => ({
            contractState: { ...state.contractState, error }
        }));
    },

    /**
     * Story 20-2 Phase 1: stash the returning-guest profile match for a specific entry,
     * or clear it on `null`. Called by guestRegistrationStore.createEntry immediately
     * after the POST returns (the captured response is the only source — the
     * post-create fetchGuests refresh does NOT carry profileMatch per Decision 23b
     * POST-only).
     *
     * Idempotent. Setting a non-null match initializes editedFieldsByEntryId[entryId]
     * to a fresh empty Set when missing. Clearing the match (`null`) ALSO drops the
     * editedFields slot — a stale edited-fields tracker for an entry whose match has
     * been invalidated would mislead buildRelationalEntryPayload on subsequent saves.
     *
     * Clearing drops `profilePhotosOfferedByEntryId` for the same reason, and it matters more.
     * That flag means "the server has already had its one attempt at THIS profile's photos and
     * attached nothing", and `resolveIdDocumentSlots` turns it into MISSING plus the line "We
     * couldn't reuse your saved photo. Please add it again." Carried across an identity edit that
     * matches a DIFFERENT profile, it convicts the new profile of a refusal that never happened:
     * the guest is told photos they have on file are unusable, and `canContinue` blocks Next —
     * before any PUT has offered them. The flag is about a match, so it dies with the match.
     */
    setProfileMatch: (entryId: string, match: ProfileMatch | null) => {
        dlog("store.setProfileMatch", match === null ? "CLEARING match" : "setting match", {
            entryId,
            ...summarizeMatch(match),
        });
        set((state) => {
            const nextProfileMatch = { ...state.profileMatchByEntryId };
            const nextEditedFields = { ...state.editedFieldsByEntryId };
            const nextOffered = { ...state.profilePhotosOfferedByEntryId };
            if (match === null) {
                delete nextProfileMatch[entryId];
                delete nextEditedFields[entryId];
                delete nextOffered[entryId];
            } else {
                nextProfileMatch[entryId] = match;
                if (!nextEditedFields[entryId]) {
                    nextEditedFields[entryId] = new Set<string>();
                }
            }
            return {
                profileMatchByEntryId: nextProfileMatch,
                editedFieldsByEntryId: nextEditedFields,
                profilePhotosOfferedByEntryId: nextOffered,
            };
        });
    },

    /**
     * Story 20-2 Phase 1: add a field name to the per-entry edited-fields set.
     * The Set guarantees idempotency — multiple edits to the same field still
     * register exactly once.
     */
    addEditedField: (entryId: string, fieldName: string) => {
        set((state) => {
            const existing = state.editedFieldsByEntryId[entryId] ?? new Set<string>();
            // Skip the state update if the field is already tracked — avoids re-renders.
            if (existing.has(fieldName)) {
                return state;
            }
            const next = new Set(existing);
            next.add(fieldName);
            return {
                editedFieldsByEntryId: {
                    ...state.editedFieldsByEntryId,
                    [entryId]: next,
                },
            };
        });
    },

    /**
     * Story 20-5 Phase 3 (AC #6): remove a field from the per-entry edited-fields
     * set. Called by MaskedProfileField on Cancel-without-typing to undo the
     * Edit-click registration — without this, a user who taps Edit, types
     * nothing, and taps Cancel would still send `editedFields: [field]` on PUT
     * with an empty value, silently wiping the stored value (data-wipe trap).
     *
     * Idempotent: removing an absent fieldName is a no-op.
     */
    removeEditedField: (entryId: string, fieldName: string) => {
        set((state) => {
            const existing = state.editedFieldsByEntryId[entryId];
            // No set yet for this entry, or the field was never tracked — nothing to do.
            if (!existing || !existing.has(fieldName)) {
                return state;
            }
            const next = new Set(existing);
            next.delete(fieldName);
            return {
                editedFieldsByEntryId: {
                    ...state.editedFieldsByEntryId,
                    [entryId]: next,
                },
            };
        });
    },

    /**
     * Claim 1 follow-up: drop the entire edited-fields slot for an entry. Used by
     * useResetProfileMatchOnIdentityEdit when the lookup-key triple changes after
     * POST — the cross-property profile matched at POST is no longer authoritative,
     * so any user-edited overlay flags from that prior match are stale.
     *
     * Idempotent: clearing an absent slot is a no-op.
     */
    clearEditedFields: (entryId: string) => {
        set((state) => {
            if (!(entryId in state.editedFieldsByEntryId)) {
                return state;
            }
            const next = { ...state.editedFieldsByEntryId };
            delete next[entryId];
            return { editedFieldsByEntryId: next };
        });
    },

    /**
     * FC-2: this entry's profile photos have now been offered to the server AND the refresh that
     * followed actually landed. Idempotent.
     */
    markProfilePhotosOffered: (entryId: string) => {
        dlog("store.markProfilePhotosOffered", "offered flag SET — empty slots become MISSING now", {
            entryId,
        });
        set((state) => {
            if (state.profilePhotosOfferedByEntryId[entryId]) return state;
            return {
                profilePhotosOfferedByEntryId: {
                    ...state.profilePhotosOfferedByEntryId,
                    [entryId]: true as const,
                },
            };
        });
    },

    resetContractState: () => {
        set(() => ({
            contractState: {
                isLoading: false,
                data: null,
                error: null,
                violations: [],
            },
            contractSigningState: {
                isLoading: false,
                isSigning: false,
                error: null,
                violations: [],
            },
        }));
    },

    refreshFormData: async (publicId: string, signal?: AbortSignal, guestEntries?: FormGuestEntry[]): Promise<boolean> => {
        if (isPreviewMode()) return false;
        try {
            const serverResponse: PublicFormDataApiDto = await formService.getForm(publicId, signal);

            if (signal?.aborted) return false;

            if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                console.warn('[refreshFormData] Invalid server response, skipping update');
                return false;
            }

            const newLocalData = convertApiFormDataToLocal(serverResponse.data, guestEntries);

            set((state) => ({
                formData: newLocalData,
                formMetadata: state.formMetadata ? {
                    ...state.formMetadata,
                    status: serverResponse.status,
                    version: serverResponse.version,
                    overallProgress: serverResponse.overallProgress,
                    signedContract: serverResponse.signedContract,
                    requiredGuestImages: serverResponse.requiredGuestImages,
                    requireContactDataFromAllGuests: serverResponse.requireContactDataFromAllGuests,
                    contractStatus: serverResponse.contractStatus,
                    rejectionReason: serverResponse.rejectionReason,
                    // Story 28-4: propagate the bilingual + multi-status
                    // messages map on every poll refresh so an Owner edit
                    // reflects to an open guest session within the polling
                    // cadence (NFR-LFM-1 2s p95). Always set (possibly empty).
                    formMessages: serverResponse.formMessages ?? {},
                    // Story 42-15: listed explicitly, or the spread above would keep the value
                    // from the first load and a switch flipped mid-session would never reach
                    // an open form. The rollback (`id_scan_enabled = false`) relies on this.
                    idScanEnabled: serverResponse.idScanEnabled === true,
                } : null,
                formState: {
                    ...state.formState,
                    currentVersion: serverResponse.version,
                    lastSavedVersion: serverResponse.version,
                },
            }));
            return true;
        } catch (error: unknown) {
            const errObj = error as { name?: string; code?: string; constraints?: Array<{ type?: string }> };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                return false;
            }
            // Re-throw NOT_FOUND so callers (polling) can detect deleted forms
            const isNotFound = errObj.constraints?.some(c => c.type === 'NOT_FOUND') || errObj.code === '404';
            if (isNotFound) {
                throw error;
            }
            console.warn('[refreshFormData] Background refresh failed:', errorLogDetail(error));
            return false;
        }
    },

}));
